import React, { useState, useCallback } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { OutlineCard } from 'src/components/Submission/MySubmission/PreferencesTab';
import { FormProvider, useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { generateValidationSchema } from 'src/utils/validation';
import {
  FormFieldDefinition,
  FormFieldTextArea
} from '@publishing-engine/domain/types/article';

import { CommandName, useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { useFormFieldSet } from 'src/hooks/useFormFieldSet';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormFieldSetModal, {
  IPathObject,
  IPathValue
} from './FormFieldSetModal';

import {
  FormFieldSelect,
  FormFieldTextInput,
  FormFieldNumberInput,
  FormFieldDateInput
} from '@publishing-engine/domain/types/article';
import ProtectedWorkflowAction from 'src/components/ProtectedAction/ProtectedWorkflowAction';
import SimpleFieldDisplay from '../Submission/GenericFieldComponents/common/SimpleFieldDisplay';
import _ from 'lodash';

interface IFieldSetProps {
  fieldNames: string[];
  label: string;
  submissionContextId: string;
  onSubmit?: (updatedValues: Record<string, unknown>) => void;
  attentionBannerText?: string;
}

export interface IFormFieldState {
  value: IPathValue;
  previousValue?: unknown;
  formFieldDefinition:
    | FormFieldNumberInput
    | FormFieldTextInput
    | FormFieldSelect
    | FormFieldDateInput
    | FormFieldTextArea;
  externalDependency?: {
    pathValue?: string;
  };
  isEditable?: boolean;
  editing?: {
    isEditable: boolean;
    via: string;
    isRequired?: boolean;
  };
}

const isFieldEditable = (field: IFormFieldState) => {
  return field.isEditable;
};

const FormFieldSet: React.FC<IFieldSetProps> = ({
  fieldNames,
  label,
  onSubmit,
  submissionContextId,
  attentionBannerText
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getFieldState } = useSubmissionFields('submission');
  const { submitFields, getCommandWithSubTypeByCommand } =
    useFormFieldSet(label);

  const {
    fields,
    defaultValues,
    schemaFields
  }: {
    fields: IFormFieldState[];
    defaultValues: IPathObject;
    schemaFields: Record<string, FormFieldDefinition>;
  } = {
    fields: [],
    defaultValues: {},
    schemaFields: {}
  };

  for (const fieldName of fieldNames) {
    const fieldState = getFieldState(fieldName) as IFormFieldState;
    if (!fieldState) continue;

    fields.push(fieldState);

    _.set(
      defaultValues,
      fieldState.formFieldDefinition.caseDataFields.main,
      fieldState.value
    );

    schemaFields[fieldState.formFieldDefinition.name] =
      fieldState.formFieldDefinition as unknown as FormFieldDefinition;
  }

  const schema = generateValidationSchema(schemaFields);

  const methods = useForm({
    defaultValues,
    resolver: ajvResolver(schema)
  });

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleSubmit = useCallback(
    async (updatedValues: Record<string, unknown>) => {
      const updates: Record<string, unknown> = {};

      // Pick only the fields that have changed
      for (const field of fields) {
        const oldValue = _.get(
          defaultValues,
          field.formFieldDefinition.caseDataFields.main
        );

        const newValue = _.get(
          updatedValues,
          field.formFieldDefinition.caseDataFields.main
        );

        if (oldValue !== newValue) {
          _.set(
            updates,
            field.formFieldDefinition.caseDataFields.main,
            newValue
          );
        }
      }

      if (Object.keys(updates).length) {
        const success = await submitFields(updates, fields);

        if (success && onSubmit) {
          onSubmit(updates);
        }
      }

      closeModal();
    },
    [submitFields, fields, onSubmit, closeModal]
  );

  const renderFieldValue = (ffd: IFormFieldState) => {
    const key = ffd.formFieldDefinition.name;

    return (
      <SimpleFieldDisplay
        key={key}
        formFieldState={ffd}
        config={ffd.formFieldDefinition}
        submissionContextId={submissionContextId}
      />
    );
  };

  // If there are no fields, don't render it
  if (fields.length === 0) {
    return null;
  }

  // Enable Edit button only if all fields inside group are editable.
  const isEditable = fields.every(isFieldEditable);

  // Field set now support only field groups editable by 1 command.
  const editViaCommand = fields.find(f => f?.editing?.via)?.editing
    ?.via as CommandName;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Flex flexDirection="column" gap={4}>
          {editViaCommand && (
            <Flex justifyContent="space-between" alignItems="center">
              <Text textStyle="headingGroup">{label}</Text>
              <ProtectedWorkflowAction
                actionName={getCommandWithSubTypeByCommand(editViaCommand)}
              >
                <ButtonText
                  onClick={openModal}
                  iconLeft={<FontAwesomeIcon icon={faEdit} />}
                  type="button"
                  isDisabled={!isEditable}
                >
                  Edit
                </ButtonText>
              </ProtectedWorkflowAction>
            </Flex>
          )}
          <OutlineCard>
            <Flex flexDirection="column" gap={6}>
              {fieldNames.map(fieldName => {
                const ffd = fields.find(
                  field => field.formFieldDefinition.name === fieldName
                );

                if (!ffd) return null;

                return renderFieldValue(ffd);
              })}
            </Flex>

            {isModalOpen && (
              <FormFieldSetModal
                isOpen={isModalOpen}
                fields={fields}
                label={label}
                onCancel={closeModal}
                onSubmit={handleSubmit}
                submissionContextId={submissionContextId}
                attentionBannerText={attentionBannerText}
                defaultValues={defaultValues}
              />
            )}
          </OutlineCard>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default FormFieldSet;
